/**
 * The review screen: source card, totals tiles, validation badges, anomalies, and
 * the department table. Rows expand to that department's journal lines.
 *
 * Employee names are never fetched or rendered — only a headcount per department.
 */
import { useState } from "react";
import { employerCost, fmtDateTime, money, type Run, type RunDept } from "../../payroll/api";

export default function RunReview({
  run, canEdit, onReplace,
}: { run: Run; canEdit: boolean; onReplace: () => void }) {
  const [open, setOpen] = useState<string | null>(null);
  const active = run.departments.filter((d) => !d.excluded);
  const balanced = run.journals.every(
    (j) => Math.abs(j.lines.reduce((a, l) => a + l.amount, 0)) < 0.005
  );

  return (
    <>
      <div className="source-card">
        <div>
          <strong>{run.fileName}</strong>
          <div className="muted">
            {run.payeMonth ? `PAYE month ${run.payeMonth} · ` : ""}run date {run.runDate} · period{" "}
            <span className="mono">{run.periodCode}</span> · {run.journals.length} journals ·{" "}
            {run.totals.people} employees
          </div>
          <div className="muted">Parsed {fmtDateTime(run.parsedUtc)} by {run.parsedBy}</div>
        </div>
        <div className="source-card__actions">
          {canEdit && <button className="btn" onClick={onReplace}>Replace file</button>}
          <a className="btn" href={`/api/payroll/runs/${run.id}/csv`}>Download CSVs</a>
        </div>
      </div>

      <div className="stat-tiles">
        <Tile label="Gross pay (incl holiday)" value={money(run.totals.gross)} />
        <Tile label="Employer NI" value={money(run.totals.erNI)} />
        <Tile label="Employer pension" value={money(run.totals.erPension)} />
        <Tile label="Total employer cost" value={money(run.totals.employerCost)} />
        <Tile label="PAYE + employee NI" value={money(run.totals.payeAndEeNI)} />
        <Tile label="Net pay to bank" value={money(run.totals.netPay)} />
        <Tile label="Employees" value={String(run.totals.people)} />
      </div>

      <div className="badge-row">
        <span className={`badge ${balanced ? "badge--ok" : "badge--bad"}`}>
          {balanced ? `✓ All ${run.journals.length} journals balance to £0.00` : "✕ A journal does not balance"}
        </span>
        <span className="badge badge--ok">✓ Column sums tie to the report’s company total</span>
        <span className="badge badge--ok">✓ Every department mapped to a nominal and cost centre</span>
        {run.journals.some((j) => j.status === "draft_created") && (
          <span className="badge badge--warn">⚠ This period already has drafts in iplicit</span>
        )}
      </div>

      {run.anomalies.length > 0 && (
        <div className="anomalies">
          <div className="anomalies__head">Flagged for your attention ({run.anomalies.length})</div>
          <ul>{run.anomalies.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </div>
      )}

      <div className="dept-table">
        <div className="dept-table__head">
          <h3>Departments — {run.journals.length} journals</h3>
          <span className="muted">No employee names are shown. Open a row to see the journal lines.</span>
        </div>

        <div className="grid-table">
          <div className="grid-table__header dept-grid">
            <div>Department</div><div>Code</div><div>Class</div><div className="num">People</div>
            <div className="num">Gross</div><div className="num">Er NI</div><div className="num">Er pens</div>
            <div className="num">Employer cost</div><div className="num">PAYE + Ee NI</div><div className="num">Net pay</div><div />
          </div>

          {active.map((d) => {
            const journal = run.journals.find((j) => j.payrollName === d.payrollName);
            const isOpen = open === d.payrollName;
            return (
              <div key={d.payrollName}>
                <div
                  className={`grid-table__row dept-grid${isOpen ? " open" : ""}`}
                  onClick={() => setOpen(isOpen ? null : d.payrollName)}
                >
                  <div className="strong">{d.payrollName}</div>
                  <div className="mono muted">{d.iplicitDept ?? "—"}</div>
                  <div><span className={`class-tag class-tag--${slug(d.class)}`}>{d.class ?? "—"}</span></div>
                  <div className="num">{d.people}</div>
                  <div className="num">{money(d.taxablePay)}</div>
                  <div className="num">{money(d.erNI)}</div>
                  <div className="num muted">{d.erPension ? money(d.erPension) : "—"}</div>
                  <div className="num strong">{money(employerCost(d))}</div>
                  <div className="num">{money(d.tax + d.eeNI)}</div>
                  <div className="num">{money(d.netPay)}</div>
                  <div className="chevron">{isOpen ? "▾" : "▸"}</div>
                </div>

                {isOpen && journal && (
                  <div className="journal-detail">
                    <div className="journal-detail__head">
                      import_doc_ref {journal.importDocRef} · {journal.description}
                      {journal.edited && <span className="tag">edited</span>}
                    </div>
                    {journal.lines.map((l, i) => (
                      <div className="journal-line" key={i}>
                        <div className="mono">{l.account}</div>
                        <div className="muted">{l.department ?? "—"}</div>
                        <div className="muted">{l.costCentre ?? "—"}</div>
                        <div>{l.description}</div>
                        <div className="num">{l.amount > 0 ? money(l.amount) : ""}</div>
                        <div className="num">{l.amount < 0 ? money(-l.amount) : ""}</div>
                      </div>
                    ))}
                    <div className="journal-line journal-line--total">
                      <div /><div /><div /><div>Balance</div>
                      <div className="num">{money(sum(journal.lines, (l) => (l.amount > 0 ? l.amount : 0)))}</div>
                      <div className="num">{money(sum(journal.lines, (l) => (l.amount < 0 ? -l.amount : 0)))}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div className="grid-table__row dept-grid grid-table__total">
            <div>Total — ties to the report’s company row</div><div /><div />
            <div className="num">{run.totals.people}</div>
            <div className="num">{money(run.totals.gross)}</div>
            <div className="num">{money(run.totals.erNI)}</div>
            <div className="num">{money(run.totals.erPension)}</div>
            <div className="num">{money(run.totals.employerCost)}</div>
            <div className="num">{money(run.totals.payeAndEeNI)}</div>
            <div className="num">{money(run.totals.netPay)}</div>
            <div />
          </div>
        </div>

        {run.excluded.length > 0 && (
          <p className="muted excluded-note">
            Excluded (no activity this period): {run.excluded.join(", ")}.
          </p>
        )}
      </div>
    </>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat-tile">
      <div className="stat-tile__label">{label}</div>
      <div className="stat-tile__value">{value}</div>
    </div>
  );
}

const sum = <T,>(rows: T[], f: (r: T) => number) => rows.reduce((a, r) => a + f(r), 0);
const slug = (c: RunDept["class"]) => (c ?? "none").toLowerCase().replace(/\s+/g, "-");
